import React from 'react';
import { BaseTool, IToolManifest, IToolComponentProps, IToolContext } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { CreateShapeCommand } from '../../core/commands/CreateShapeCommand';
import { Button } from '../../components/ui/button';
import { Type } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTranslation } from '@/hooks/useTranslation';
import i18n from '@/i18n/config';

const TextToolComponent: React.FC<IToolComponentProps> = ({ onExecute }) => {
  const { t } = useTranslation('diagram');
  const handleClick = () => {
    // Trigger activation via registry; execution is a no-op for this tool.
    onExecute({});
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            data-testid="tid.tools.text.trigger"
            className="w-10 h-10 rounded-lg"
            onClick={handleClick}
          >
            <Type className="h-5 w-5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p>{t('tools.addTextTooltip', { defaultValue: 'Add text (T)' })}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

};

export class TextTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'text',
    name: 'tools.text',
    icon: 'type',
    category: 'create',
    description: 'Add text',
    version: '1.0.0',
    
    shortcuts: [
      {
        key: 'T',
        modifiers: [],
        description: 'Activate text tool'
      }
    ],
    
    ui: {
      toolbar: true,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: false,
      statusBar: false
    },
    
    capabilities: {
      modifiesShapes: false,
      createsShapes: true,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: false,
      supportsBatchOperations: false
    }
  };

  // Stage click handler bound on activation
  private handleStageClick = () => {
    const stage = this.context?.stage;
    const layer = this.context?.layer;
    if (!stage || !layer) return;
    const pos = stage.getPointerPosition();
    if (!pos) return;
    const scale = stage.scaleX() || 1;
    const offset = stage.position();
    const x = (pos.x - offset.x) / scale;
    const y = (pos.y - offset.y) / scale;
    const cmd: ICommand = new CreateShapeCommand({
      type: 'text',
      x,
      y,
      width: 120,
      height: 32,
      text: i18n.t('diagram:tools.defaultNewText', { defaultValue: 'Text' }) as string,
      fontSize: 16,
      fontFamily: 'Noto Sans',
      fillColor: 'transparent',
      strokeColor: 'transparent',
      ydoc: this.context?.doc, // Pass Yjs document for persistence
      category: 'text'
    });
    try { this.context?.eventBus.emit('command:execute', cmd); } catch {}
    // After creating one text, switch back to selection
    try { this.context?.eventBus.emit('tool:activate', { id: 'genogram-selection' }); } catch {}
  };

  onActivate(context: IToolContext): void {
    super.onActivate(context);
    try { document.body.style.cursor = 'text'; } catch {}
    // Ensure canvas shows text cursor (element styles override body)
    const stageAny = this.context?.stage as any;
    if (stageAny && stageAny.container) {
      try {
        const container = stageAny.container();
        container.setAttribute('data-text-cursor', '1');
        container.style.cursor = 'text';
      } catch {}
    }
    // Attach one global click handler on the stage namespace-scoped to this tool
    const st = this.context?.stage as any;
    if (st && typeof st.on === 'function') {
      try { st.on('mousedown.text-tool click.text-tool tap.text-tool', this.handleStageClick); } catch {}
    }
  }

  onDeactivate(): void {
    // Detach handlers and restore cursor
    const stage = this.context?.stage as any;
    if (stage && typeof stage.off === 'function') {
      try { stage.off('.text-tool'); } catch {}
    }
    try {
      if (stage && stage.container) {
        const container = stage.container();
        container.removeAttribute('data-text-cursor');
        container.style.cursor = 'default';
      }
    } catch {}
    try { document.body.style.cursor = 'default'; } catch {}
    super.onDeactivate();
  }

  // Execute is a no-op; creation happens on canvas click while active
  execute(): ICommand | null {
    return null;
  }

  getToolbarComponent() {
    return TextToolComponent;
  }
}
